<script setup lang="ts">
import { computed } from "vue";
import { vIntersectionObserver } from "@vueuse/components";
import { useAnalyticManager } from "@magnit/analytic-events";
import VButton from "@magnit/core/src/components/VButton/VButton.vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VStack from "@magnit/core/src/components/VStack/VStack.vue";
import VStackItem from "@magnit/core/src/components/VStack/VStackItem.vue";
import VHidden from "@magnit/core/src/components/VHidden/VHidden.vue";
import QrMyMagnit from "@magnit/unit-footer/src/assets/svg/qr/my-magnit.svg?url";
import QrMyMagnitInner from "@magnit/unit-footer/src/assets/svg/qr/my-magnit-inner.svg?url";
import QrMyMagnitCosmetic from "@magnit/unit-footer/src/assets/svg/qr/my-magnit-cosmetic.svg?url";
import QrAppMagnit from "@magnit/unit-footer/src/assets/svg/qr/app-magnit.svg?url";
import QrMyMagnitLandingMagnitID from "@magnit/unit-footer/src/assets/svg/qr/my-magnit-landing-magnit-id.svg?url";
import IconAppStore from "@magnit/unit-footer/src/assets/svg/stores/app-store.svg?url";
import IconGooglePlay from "@magnit/unit-footer/src/assets/svg/stores/google-play.svg?url";
import IconRuStore from "@magnit/unit-footer/src/assets/svg/stores/ru-store.svg?url";
import IconAppGallery from "@magnit/unit-footer/src/assets/svg/stores/app-gallery.svg?url";
import { linkMyMagnit, linksStores } from "@magnit/unit-footer/src/footer.vars";
import type { IFooterProject } from "@magnit/unit-footer/src/footer.types";

const props = withDefaults(
  defineProps<{
    project: IFooterProject;
  }>(),
  {
    project: "magnit",
  },
);

const links = [
  {
    title: "App Store",
    img: IconAppStore,
    link: linksStores.appStore,
  },
  {
    title: "Rustore",
    img: IconRuStore,
    link: linksStores.appStore,
  },
  {
    title: "Google Play",
    img: IconGooglePlay,
    link: linksStores.googlePlay,
  },
  {
    title: "App Gallery",
    img: IconAppGallery,
    link: linksStores.appGallery,
  },
];

const qrMPlusSrc = computed(() => {
  switch (props.project) {
    case "my-magnit":
      return QrMyMagnitInner;
    case "cosmetic":
      return QrMyMagnitCosmetic;
    case "landing-magnit-id":
      return QrMyMagnitLandingMagnitID;
    default:
      return QrMyMagnit;
  }
});

// Analytics
const { send } = useAnalyticManager();
const onVisibility = ([{ isIntersecting }]: IntersectionObserverEntry[]) => {
  if (isIntersecting) {
    send("Footer:ToProfile:View");
  }
};
</script>

<template>
  <VStack
    v-intersection-observer="onVisibility"
    class="unit-footer-apps"
    :class="`unit-footer-apps_${project}`"
    :gap="{ xs: '24px', s: '32px', ml: '20px' }"
    :direction="{ xs: 'column', ml: 'row' }"
    align="stretch"
  >
    <VStackItem
      class="unit-footer-app unit-footer-app_my-magnit"
      :size="{ xs: 12, ml: 6 }"
    >
      <VStack
        :gap="{ xs: '8px 12px', l: '4px 8px' }"
        direction="row"
        :wrap="{ xs: 'nowrap', l: 'wrap' }"
      >
        <VStackItem>
          <div class="unit-footer-app__icon">
            <img
              src="./assets/images/logo-magnit-online-app.webp"
              alt="Приложение Магнит Онлайн"
            />
          </div>
        </VStackItem>

        <VStackItem>
          <div class="unit-footer-app__description">
            <VText
              tag="div"
              :font="{
                xs: 'title-medium',
                ml: 'title-large',
                l: 'title-small',
              }"
            >
              Магнит Онлайн
            </VText>
            <VHidden :hide="{ xs: false, l: true }">
              <VText tag="div" font="body-small-regular">
                Карта Магнит Плюс в телефоне.
                <span class="no-wrap">Не занимает места</span>
              </VText>
            </VHidden>
          </div>
        </VStackItem>
        <VHidden :hide="{ xs: true, l: false }">
          <VStackItem>
            <VText tag="div" font="body-small-regular">
              Карта Магнит Плюс в телефоне.
              <span class="no-wrap">Не занимает места</span>
            </VText>
          </VStackItem>
        </VHidden>
      </VStack>

      <VHidden :hide="{ xs: false, l: true }">
        <div class="unit-footer-app__button-wrapper">
          <a
            :href="linkMyMagnit"
            rel="noreferrer"
            target="_blank"
            @click="send('Footer:ToProfile:ToWeb:Click')"
          >
            <VButton theme="secondary">Открыть веб-версию</VButton>
          </a>
        </div>
      </VHidden>

      <VHidden :hide="{ xs: true, l: false }">
        <div class="unit-footer-app__qr-wrapper">
          <img :src="qrMPlusSrc" :alt="`QR code ${project}`" />
        </div>
        <a v-if="project !== 'my-magnit'" :href="linkMyMagnit">
          <VButton tag="span">Узнать больше</VButton>
        </a>
      </VHidden>
    </VStackItem>

    <VStackItem
      class="unit-footer-app"
      :size="{ xs: 12, ml: project === 'my-magnit' ? 12 : 6, l: 6 }"
    >
      <VStack
        :gap="{ xs: '12px', l: '8px' }"
        direction="row"
        :wrap="{ xs: 'nowrap', l: 'wrap' }"
      >
        <div class="unit-footer-app__icon">
          <img
            src="./assets/images/logo-magnit-app.webp"
            alt="Приложение Магнит"
          />
        </div>
        <div class="unit-footer-app__description">
          <VText
            tag="div"
            :font="{ xs: 'title-medium', ml: 'title-large', l: 'title-small' }"
          >
            Приложение Магнит
          </VText>
          <VHidden :hide="{ xs: false, l: true }">
            <VText tag="div" font="body-small-regular">
              Акции, доставка и больше бонусов за покупки
            </VText>
          </VHidden>
        </div>
        <VHidden :hide="{ xs: true, l: false }">
          <VText tag="div" font="body-small-regular">
            Акции, доставка и больше бонусов за покупки
          </VText>
        </VHidden>
      </VStack>

      <VHidden :hide="{ xs: false, l: true }">
        <div class="unit-footer-app__button-wrapper">
          <a
            :href="linksStores.auto"
            rel="noreferrer"
            target="_blank"
            @click="send('Footer:ToProfile:ToApp:Click')"
          >
            <VButton theme="secondary">Скачать приложение</VButton>
          </a>
        </div>
      </VHidden>

      <VHidden :hide="{ xs: true, l: false }">
        <div class="unit-footer-app__qr-wrapper">
          <img :src="QrAppMagnit" alt="QR code download Magnit app" />
        </div>
        <VText
          font="caption-large-regular-low"
          color="secondary"
          class="unit-footer-app__qr-app-desc"
          >Наведите камеру на QR-код<br />
          и скачайте приложение
        </VText>
      </VHidden>
      <VHidden :hide="{ xs: true, l: false }">
        <div class="unit-footer-app__links">
          <a
            v-for="item in links"
            :key="item.title"
            :href="item.link"
            target="_blank"
            rel="noreferrer"
            :aria-label="item.title"
          >
            <img :src="item.img" :alt="item.title" />
          </a>
        </div>
      </VHidden>
    </VStackItem>
  </VStack>
</template>

<style lang="postcss" scoped>
.unit-footer-apps {
  padding-top: 24px;

  .no-wrap {
    white-space: nowrap;
  }
}

.unit-footer-app {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;

  &_my-magnit {
    .unit-footer-apps_my-magnit & {
      display: none;
    }
  }

  &__icon {
    display: flex;

    & img {
      width: 64px;
      height: 64px;
    }
  }

  &__button-wrapper {
    & button {
      width: 100%;
    }
  }

  &__qr-wrapper {
    display: flex;
    padding: 12px;
    border-radius: 12px;
    border: 1px solid var(--pl-border-secondary-default);
    width: fit-content;
    margin-bottom: 12px;

    img {
      width: 118px;
      height: 118px;
    }
  }

  &__qr-app-desc {
    display: flex;
    flex-direction: column;
  }

  &__links {
    display: flex;
    gap: 12px;

    a {
      width: 56px;
      height: 56px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 12px;
      background-color: var(--pl-surface-muted-default);

      &:hover {
        background-color: var(--pl-surface-muted-hover);
      }

      &:active {
        background-color: var(--pl-surface-muted-pressed);
      }
    }
  }
}

@media (--pl-viewport-from-ml) {
  .unit-footer-app {
    gap: 16px;
  }
}

@media (--pl-viewport-from-l) {
  .unit-footer-app {
    padding-bottom: 32px;
    justify-content: flex-start;
    gap: 12px;

    &_my-magnit {
      .unit-footer-apps_my-magnit & {
        display: flex;
      }
    }

    &__links {
      gap: 8px;
    }

    &__icon img {
      width: 32px;
      height: 32px;
    }

    &__button-wrapper {
      & button {
        width: auto;
      }
    }
  }
}

@media (--pl-viewport-from-xl) {
  .unit-footer-app {
    &__links {
      gap: 12px;
    }
  }
}
</style>
